import { useState, useEffect } from "react";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import Swal from "sweetalert2";
import { THEME_COLOR } from "../../config/club";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function EditCoverPhotoModal({
    user,
    setProfile,
    profile,
    setUser,
    onClose
}) {
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState(
        profile?.coverPhoto || "/img/bg.png"
    );
    const [saving, setSaving] = useState(false);

    const storage = getStorage();
    const THEME_VIOLET = THEME_COLOR;

    useEffect(() => {
        return () => {
            if (coverPreview?.startsWith("blob:")) {
                URL.revokeObjectURL(coverPreview);
            }
        };
    }, [coverPreview]);

    const saveCover = async () => {
        if (!coverFile) {
            return Swal.fire("No Image", "Please select a cover photo.", "warning");
        }

        setSaving(true);

        try {
            const coverRef = ref(storage, `covers/${user.uid}/cover.jpg`);
            await uploadBytes(coverRef, coverFile);
            const coverURL = await getDownloadURL(coverRef);

            await updateDoc(doc(db, "users", user.uid), {
                coverPhoto: coverURL,
                updatedAt: serverTimestamp()
            });

            setUser((prev) => ({
                ...prev,
                coverPhoto: coverURL
            }));
            setProfile((prev) => ({
                ...prev,
                coverPhoto: coverURL
            }));
            onClose(coverURL);
            Swal.fire({
                toast: true,
                position: 'center',
                icon: 'success',
                title: 'Cover photo updated',
                showConfirmButton: false,
                timer: 2000
            });
        } catch (err) {
            Swal.fire("Error", err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center px-3"
            style={{
                zIndex: 1045,
                backgroundColor: "rgba(0,0,0,0.6)",
                backdropFilter: "blur(8px)"
            }}
        >
            <div
                className="rounded-4 shadow-lg w-100 position-relative overflow-hidden"
                style={{
                    maxWidth: 520,
                    backgroundColor: "var(--card-bg)",
                    color: "var(--text-color)"
                }}
            >
                {/* Accent Bar */}
                <div style={{ height: "6px", backgroundColor: THEME_VIOLET }}></div>

                <div className="p-4">

                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="fw-black mb-0" style={{ color: THEME_VIOLET }}>
                            Edit Cover Photo
                        </h4>
                        <button className="btn-close" onClick={() => onClose(false)} />
                    </div>

                    {/* Cover Preview */}
                    <div className="mb-4">
                        <div
                            className="rounded-4 overflow-hidden border"
                            style={{
                                height: "180px",
                                backgroundImage: `url(${coverPreview})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                borderColor: "var(--border-color)"
                            }}
                        />
                    </div>

                    {/* Upload Button */}
                    <div className="mb-4 text-center">
                        <label
                            htmlFor="coverUpload"
                            className="btn btn-outline-secondary rounded-pill px-4"
                            style={{
                                borderColor: "var(--border-color)",
                                color: "var(--text-color)"
                            }}
                        >
                            <i className="bi bi-image me-2"></i>
                            Change Cover
                        </label>

                        <input
                            id="coverUpload"
                            type="file"
                            hidden
                            accept="image/png, image/jpeg, image/webp, image/gif"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;

                                // 1️⃣ File type validation
                                const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
                                if (!allowedTypes.includes(file.type)) {
                                    Swal.fire("Invalid File", "Only JPG, PNG, WEBP, or GIF allowed.", "warning");
                                    return;
                                }

                                // 2️⃣ File size validation (max 5MB)
                                const maxSize = 4 * 1024 * 1024; // 4MB
                                if (file.size > maxSize) {
                                    Swal.fire("Too Large", "Image must be under 4MB.", "warning");
                                    return;
                                }

                                // 3️⃣ Optional: Minimum dimension validation
                                const img = new Image();
                                img.onload = () => {
                                    setCoverFile(file);
                                    setCoverPreview(URL.createObjectURL(file));
                                };

                                img.src = URL.createObjectURL(file);
                            }}
                        />
                    </div>

                    {/* Save Button */}
                    <button
                        className="btn btn-lg w-100 fw-bold text-white rounded-pill shadow-sm py-3 mb-2"
                        style={{ backgroundColor: THEME_VIOLET }}
                        onClick={saveCover}
                        disabled={saving}
                    >
                        {saving ? "Saving Cover..." : "Save Cover"}
                    </button>

                    <button
                        className="btn btn-link w-100 text-muted text-decoration-none small"
                        onClick={() => onClose(false)}
                    >
                        Cancel
                    </button>
                </div>
            </div>

            <style>{`
        .fw-black { font-weight: 900; }
      `}</style>
        </div>
    );
}